import { Pencil, Truck, User, Route as RouteIcon, Clock, PenLine, StickyNote } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import type { Expense } from '../../types/expense';
import { useExpenseTransitions } from '../../hooks/useExpenseTransitions';
import { useExpenseReceipts } from '../../hooks/useExpenseReceipts';
import { ExpenseTransitions } from './ExpenseTransitions';
import { ReceiptViewer } from './ReceiptViewer';
import { colorDeCategoria, formatearCOP } from '../../utils/expenses';
import { formatDateTime } from '../../utils/trips';

/**
 * FE-12 — el detalle de un gasto, con su recibo y su novedad.
 *
 * Las dos se piden aquí y no en el listado: la inmensa mayoría de los gastos
 * nunca se corrigen, y una URL firmada por fila serían N+1 peticiones para
 * enlaces que además caducan.
 *
 * Los dos instantes se muestran SIEMPRE juntos y con nombres distintos —"se
 * hizo" y "se registró"—, incluso cuando coinciden. Es la diferencia que
 * explica por qué un peaje digitado hoy aparece en el expediente de anteayer, y
 * esconderla cuando son iguales enseñaría a leerla mal.
 *
 * `onReciboSubido` devuelve el GASTO recargado: quien muestre la lista tiene
 * que reemplazarlo, o su fila seguirá marcada «sin recibo».
 */
export function ExpenseDetailModal({
  expense,
  onClose,
  onCorregir,
  puedeCorregir,
  ventanaVencida = false,
  puedeSubirRecibo,
  onReciboSubido,
}: {
  expense: Expense | null;
  onClose: () => void;
  onCorregir?: (expense: Expense) => void;
  puedeCorregir: boolean;
  /** issue #150: el rol permite corregir, pero al Operador se le venció la ventana de 2 semanas. */
  ventanaVencida?: boolean;
  puedeSubirRecibo: boolean;
  onReciboSubido?: (expense: Expense) => void;
}) {
  const { transitions, isLoading, error } = useExpenseTransitions(expense?.id ?? null);
  const recibos = useExpenseReceipts(expense?.id ?? null);

  if (!expense) return null;

  const color = colorDeCategoria(expense.categoryCode);

  return (
    <Modal
      isOpen={!!expense}
      onClose={onClose}
      title="Gasto"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cerrar</Button>
          {puedeCorregir && onCorregir && (
            ventanaVencida ? (
              <Button leftIcon={<Pencil size={14} />} disabled title="Pasaron más de 2 semanas desde el registro: ya no se puede corregir.">
                Corregir
              </Button>
            ) : (
              <Button leftIcon={<Pencil size={14} />} onClick={() => onCorregir(expense)}>
                Corregir
              </Button>
            )
          )}
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {/* issue #150: nunca en silencio — si el rol permite corregir pero la
            ventana venció, el mensaje va explícito en el cuerpo, no solo en el
            tooltip del botón deshabilitado. */}
        {puedeCorregir && ventanaVencida && (
          <div style={{ padding: '0.75rem 1rem', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 'var(--radius-lg)', color: '#F59E0B', fontSize: '12.5px' }}>
            Pasaron más de 2 semanas desde el registro — este gasto quedó en solo lectura.
          </div>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem' }}>
          <span
            style={{
              fontSize: '12px', fontWeight: 800, color,
              background: `${color}1F`, border: `1px solid ${color}59`,
              borderRadius: '99px', padding: '3px 11px',
            }}
          >
            {expense.categoryLabel}
          </span>
          <strong style={{ fontSize: '22px', fontWeight: 800, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
            {formatearCOP(expense.amount)}
          </strong>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.625rem' }}>
          <Dato icono={<Clock size={13} />} etiqueta="Se hizo" valor={formatDateTime(expense.incurredAt)} />
          <Dato icono={<PenLine size={13} />} etiqueta="Se registró" valor={formatDateTime(expense.createdAt)} />
          {expense.vehiclePlate && <Dato icono={<Truck size={13} />} etiqueta="Vehículo" valor={expense.vehiclePlate} />}
          {expense.driverName && <Dato icono={<User size={13} />} etiqueta="Conductor" valor={expense.driverName} />}
          {expense.tripId && <Dato icono={<RouteIcon size={13} />} etiqueta="Viaje" valor="asignado explícitamente" />}
          {expense.notes && <Dato icono={<StickyNote size={13} />} etiqueta="Nota" valor={expense.notes} />}
        </div>

        <ReceiptViewer
          receipts={recibos.receipts}
          isLoading={recibos.isLoading}
          error={recibos.error}
          subiendo={recibos.subiendo}
          urlDeDescarga={recibos.urlDeDescarga}
          onSubir={puedeSubirRecibo ? async (file) => {
            const actualizado = await recibos.subir(file);
            onReciboSubido?.(actualizado);
          } : undefined}
        />

        <ExpenseTransitions transitions={transitions} isLoading={isLoading} error={error} />
      </div>
    </Modal>
  );
}

function Dato({ icono, etiqueta, valor }: { icono: React.ReactNode; etiqueta: string; valor: string }) {
  return (
    <div style={{ background: 'var(--bg-raised)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '0.5rem 0.75rem' }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: 'var(--text-muted)' }}>
        {icono} {etiqueta}
      </span>
      <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>{valor}</span>
    </div>
  );
}
